// The deployment topology: which worker serves which hosts, per app, per
// deploy target, and which peers each app speaks to at that target.
//
// Three targets, matching @bindery/shared/ledger's LEDGER_ENVS:
//
//   dev      X.fugl.dev            worker X-fugl           wrangler top level
//   preview  X-preview.fugl.dev    worker X-fugl-preview   env.preview
//   prod     X.read-by-ear.com     worker X                env.prod
//
// The wrangler top level is dev on purpose: a bare `wrangler deploy` lands on
// staging, and production is reachable only through `env.prod` plus
// --allow-production.
//
// wrangler.jsonc cannot import TypeScript, so its values stay literal and
// scripts/wrangler.test.ts in each app asserts them against this table.
//
// A deployment cannot learn its own target from Cloudflare or Convex, so each
// wrangler scope and Convex deployment states it as a `DEPLOY_TARGET` var;
// `@bindery/config/wrangler-invariants` checks the var against its scope
// (docs/ops/deploy.md § The deploy target a running deployment reports).
//
// Pure data and pure functions with no runtime dependencies: the browser
// bundle and the Workers both import this.

export const DEPLOY_TARGETS = ["dev", "preview", "prod"] as const;
export type DeployTarget = (typeof DEPLOY_TARGETS)[number];

/**
 * Whether `value` is one of the three targets. The `DEPLOY_TARGET` var is an
 * unvalidated string, so every caller fails closed on null: a broker that
 * cannot say which target it is serves no hosts rather than all of them.
 */
export function isDeployTarget(value: unknown): value is DeployTarget {
  return (
    typeof value === "string" &&
    (DEPLOY_TARGETS as readonly string[]).includes(value)
  );
}

export const TOPOLOGY_APPS = [
  "auth",
  "checkout",
  "retells",
  "corpus",
  // Reached only by other Workers, never a browser: no `PeerSet` row. In the
  // table because a misdirected deploy would point a PII service at the wrong
  // database.
  "secure",
  // Shares its hosts with `study` and `go`; see `paths` on AppDeployment.
  "www",
  // Path-scoped on www's hosts: "the catch-all does not swallow the funnel" is
  // a claim about the two rows together.
  "study",
  // Attaches by both mechanisms at once; see `AppDeployment.routeClaims`.
  "go",
  // Production is yominoki.fugl.dev by decision, so prod takes the bare name
  // the dev target holds elsewhere and dev moves to yominoki-dev.fugl.dev.
  // Auth is atproto OAuth, so no `PeerSet` row.
  "yominoki",
  // The old repo's D1 queue, DO cursor, and CockroachDB are live shared
  // infra that only one deployment may own, so no target deploys until the
  // cutover (docs/plan/yominoki-port.md, decision 6); staging rows reserve
  // names rather than invite a second concurrent tail.
  "yominoki-ingest",
  // A broker consumer, so it needs a row, and `TOPOLOGY` is total over every
  // target. Only dev is meant to deploy; preview and prod reserve names so a
  // stray `--env` lands on a checked identity.
  "speech-lab",
  // Production is lekterion.fugl.dev, as for yominoki: prod takes the bare
  // name and dev moves to lekterion-dev.fugl.dev. Production signs in through
  // SOURCE_PEERS, not its own target's broker (apps/lekterion/src/auth/broker.ts).
  "lekterion",
  // A broker consumer on the `reader_session` lane.
  "reader",
  // A cron Worker with no browser caller; its host only satisfies the
  // every-scope-attaches-routes invariant. Only one scope per Convex
  // deployment may run its cron (apps/research-ingest/wrangler.jsonc).
  "research-ingest",
  // The speech_tokenizer route, called only by other Workers with a function
  // key: no `PeerSet` row. Staging only; see STAGING_ONLY_APPS.
  "speech-tokenizer",
  // The founder's teleprompter: a broker consumer, staging only by founder
  // decision 2026-10-17; see STAGING_ONLY_APPS.
  "stagecraft",
  // An adult's print-and-grade tool on the `user_session` lane, reading and
  // writing convex-reader from the browser.
  "worksheets",
  // Three retired hosts answered by one script; see `stagingRedirects`.
  "redirects",
  // The teacher landing page; teacher sign-in and the class dashboard stay on
  // the broker's `/classroom`.
  "classroom",
] as const;
export type TopologyApp = (typeof TOPOLOGY_APPS)[number];

/**
 * Apps with NO production entry at all, as opposed to a reserved one.
 *
 * A reserved entry claims a worker name (deployed identity) and a host.
 *
 * `TOPOLOGY.prod` is typed over `ProductionApp`, so a prod entry for one of
 * these does not compile. Giving one a production deploy moves it out of
 * this list and adds its prod entry in the same commit.
 */
export const STAGING_ONLY_APPS = [
  // Not deployed yet; the Azure function serves production callers until a
  // production worker and host are decided.
  "speech-tokenizer",
  // A founder-only tool: stagecraft.fugl.dev is its one deployment.
  "stagecraft",
] as const satisfies readonly TopologyApp[];
export type StagingOnlyApp = (typeof STAGING_ONLY_APPS)[number];
/** Every app with a production entry in `TOPOLOGY.prod`. */
export type ProductionApp = Exclude<TopologyApp, StagingOnlyApp>;
/** The two targets every app has an entry at. */
export type StagingTarget = Exclude<DeployTarget, "prod">;

/** Whether `app` has no production deploy (see STAGING_ONLY_APPS). */
export function isStagingOnly(app: TopologyApp): app is StagingOnlyApp {
  return (STAGING_ONLY_APPS as readonly TopologyApp[]).includes(app);
}

/** One app at one target: the worker that serves it and the hosts it holds. */
export interface AppDeployment {
  /**
   * The Cloudflare worker name, which is the deployed identity: `wrangler
   * deploy` replaces the script of whatever worker holds this name, so a name
   * equal to a live production worker IS a cutover.
   */
  readonly worker: string;
  /**
   * The hosts the worker answers on; the first is canonical. Without `paths`
   * they are wrangler custom domains; with it they are zone routes onto hosts
   * whose DNS record another row's custom domain provisioned.
   */
  readonly hosts: readonly [string, ...string[]];
  /**
   * Path prefixes this app claims on those hosts; absent means the whole host.
   *
   * `apps/www` and `apps/study` share one pair of hostnames because `qrUrl`
   * (`https://read-by-ear.com/start/<id>`) is printed on bookmarks already in
   * circulation (docs/plan/study-app-port.md).
   *
   * Three pattern shapes, each meaning one thing (`pathMatches`): `/enroll`
   * is an exact claim, `/enroll/*` the subtree but not the bare path, and
   * `/enroll*` a prefix glob. A page with children is claimed by the glob,
   * not exact-plus-subtree, because Cloudflare matches a route against the
   * full URL including the query string and only a `*`-terminated pattern
   * can match one that carries it.
   *
   * Uniqueness: every (host, path-scope) pair has one owner, and at most one
   * app per host may omit `paths`.
   *
   * A whole-host claim is a custom domain (Cloudflare provisions the record
   * and certificate); a path-scoped claim is a zone route that needs the
   * proxied record to already exist, which www's custom domain provides.
   * Cloudflare documents the two coexisting, most specific winning.
   * https://developers.cloudflare.com/workers/configuration/routing/routes/
   *
   * Unverified: no deploy from this workspace has yet put two Workers on one
   * hostname; docs/plan/study-app-port.md names the curl checks.
   */
  readonly paths?: readonly [string, ...string[]];
  /**
   * Path-scoped claims on hosts some OTHER row serves whole, for a row that
   * owns whole hosts of its own and ALSO claims a prefix elsewhere (`paths`
   * scopes every host in `hosts`, so it cannot spell that).
   *
   * `go` answers `/go/<slug>` on the apexes with a 308 to its own host. A
   * second pseudo-app row sharing the worker name was rejected because a
   * worker name is deployed identity and a row is a deploy.
   *
   * Claims are zone routes with the same pattern shapes as `paths`; a claim
   * on a host no row serves whole has no DNS record to attach to
   * (topology.test.ts refuses one). They join `routePatternsAt` and
   * `appOfRequest` but NOT `hostsAt`, `hostsOf`, `appOfHost` or
   * `targetOfHost`: a worker asking "did this arrive on a host of my own?"
   * would be misled by the claimed host.
   */
  readonly routeClaims?: readonly RouteClaim[];
}

/** One `AppDeployment.routeClaims` entry: a host and the paths claimed on it. */
export interface RouteClaim {
  /** A host some OTHER row at the same target serves whole. */
  readonly host: string;
  /** The claimed patterns — the same grammar and meanings as `paths`. */
  readonly paths: readonly [string, ...string[]];
}

/** The broker JWT audience every consumer pins (the broker's DEFAULT_AUDIENCE). */
export const BROKER_AUDIENCE = "sara.ai";

/**
/**
 * The peers an app speaks to. Issuers are bare hosts: the broker stamps a
 * bare-domain `iss` (Convex's customJwt provider expects that) and checkout
 * stamps its own serving host the same way.
 */
export interface PeerSet {
  readonly authOrigin: string;
  readonly authIssuer: string;
  readonly authJwks: string;
  readonly checkoutOrigin: string;
  readonly checkoutIssuer: string;
}

const staging = (app: TopologyApp, suffix: "" | "-preview"): AppDeployment => ({
  worker: `${app}-fugl${suffix}`,
  hosts: [`${app}${suffix}.fugl.dev`],
});

/**
 * The paths `apps/study` claims wherever `apps/www` is served.
 *
 * `/enroll`, `/start` and `/reading-check` are pages, and www is an
 * assets-only Worker, so a claim that lets one fall through is a hard 404.
 * Cloudflare matches a route against the full URL including the query
 * string, so only the glob also answers `/enroll?utm_source=...`, an exact
 * claim hands it to www's 404. The glob overclaims `/enrollment` and
 * `/starter`, but no such page or asset exists; a collision belongs in a test
 * beside www's own routes.
 *
 * `/print/*` stays a subtree claim: no page sits directly under `/print`, so
 * a glob would only move a 404 between Workers. If one is added this becomes
 * `/print*` in the same commit.
 *
 * One constant because staging must mirror the production shape or it cannot
 * answer the question staging exists to answer. `/start*` has an external
 * contract: the printed QR target sits under it.
 */
const STUDY_PATHS = [
  "/enroll*",
  "/start*",
  "/print/*",
  "/reading-check*",
] as const;

/**
 * `apps/study` at a staging target: the SAME host www holds there, scoped to
 * the study paths. `staging("study", …)` would deploy cleanly on its own
 * host and never exercise two Workers sharing one hostname.
 */
const stagingStudy = (suffix: "" | "-preview"): AppDeployment => ({
  worker: `study-fugl${suffix}`,
  hosts: [`www${suffix}.fugl.dev`],
  paths: [...STUDY_PATHS],
});

/**
 * The paths `apps/go` claims on every apex `apps/www` serves, each answered
 * with a 308 to the go host in the same zone (docs/plan/go-app-port.md).
 *
 * Exact-plus-subtree, not the glob: `/go*` would claim
 * `apps/www/public/goblins.jpg`, which www's manifest test turns into a
 * failing build. The cost is that a bare `/go?utm_source=...` slips to
 * www's 404, accepted because bare `/go` carries no printed contract.
 *
 * One constant for the same reason as STUDY_PATHS.
 */
const GO_APEX_PATHS = ["/go", "/go/*"] as const;

/**
 * `apps/go` at a staging target: its own host as a custom domain, plus the
 * `/go` claims on the same host www holds there, for stagingStudy's reason.
 */
const stagingGo = (suffix: "" | "-preview"): AppDeployment => ({
  ...staging("go", suffix),
  routeClaims: [{ host: `www${suffix}.fugl.dev`, paths: [...GO_APEX_PATHS] }],
});

const stagingRedirects = (suffix: "" | "-preview"): AppDeployment => ({
  worker: `redirects-fugl${suffix}`,
  hosts: [
    `words${suffix}.fugl.dev`,
    `parent${suffix}.fugl.dev`,
    `my${suffix}.fugl.dev`,
  ],
});

/** Total over every app at the staging targets, and over `ProductionApp` at prod. */
export interface Topology {
  readonly dev: Readonly<Record<TopologyApp, AppDeployment>>;
  readonly preview: Readonly<Record<TopologyApp, AppDeployment>>;
  readonly prod: Readonly<Record<ProductionApp, AppDeployment>>;
}

export const TOPOLOGY: Topology = {
  dev: {
    auth: staging("auth", ""),
    checkout: staging("checkout", ""),
    retells: staging("retells", ""),
    corpus: staging("corpus", ""),
    secure: staging("secure", ""),
    www: staging("www", ""),
    study: stagingStudy(""),
    go: stagingGo(""),
    // `staging("yominoki", "")` would land dev on the production host.
    yominoki: { worker: "yominoki-fugl", hosts: ["yominoki-dev.fugl.dev"] },
    "yominoki-ingest": {
      worker: "yominoki-ingest-fugl",
      hosts: ["yominoki-ingest-dev.fugl.dev"],
    },
    "speech-lab": staging("speech-lab", ""),
    // `staging("lekterion", "")` would land dev on the production host.
    lekterion: { worker: "lekterion-fugl", hosts: ["lekterion-dev.fugl.dev"] },
    reader: staging("reader", ""),
    "research-ingest": staging("research-ingest", ""),
    "speech-tokenizer": staging("speech-tokenizer", ""),
    stagecraft: staging("stagecraft", ""),
    worksheets: staging("worksheets", ""),
    redirects: stagingRedirects(""),
    classroom: staging("classroom", ""),
  },
  preview: {
    // Stage broker and checkout changes on their own hosts; consumers pin
    // them only when switched (allowedPeersFor), never by default (peersFor).
    auth: staging("auth", "-preview"),
    checkout: staging("checkout", "-preview"),
    retells: staging("retells", "-preview"),
    corpus: staging("corpus", "-preview"),
    secure: staging("secure", "-preview"),
    www: staging("www", "-preview"),
    study: stagingStudy("-preview"),
    go: stagingGo("-preview"),
    yominoki: staging("yominoki", "-preview"),
    "yominoki-ingest": staging("yominoki-ingest", "-preview"),
    "speech-lab": staging("speech-lab", "-preview"),
    // Declared in apps/lekterion/wrangler.jsonc, but nothing deploys it.
    lekterion: staging("lekterion", "-preview"),
    reader: staging("reader", "-preview"),
    "research-ingest": staging("research-ingest", "-preview"),
    "speech-tokenizer": staging("speech-tokenizer", "-preview"),
    // Reserved, because the table is total over the staging targets; nothing
    // deploys it.
    stagecraft: staging("stagecraft", "-preview"),
    worksheets: staging("worksheets", "-preview"),
    redirects: stagingRedirects("-preview"),
    classroom: staging("classroom", "-preview"),
  },
  prod: {
    // The sara.ai mirrors are second domains of the same worker; the issuer
    // a token or receipt carries is the host that served it.
    auth: { worker: "auth", hosts: ["auth.read-by-ear.com", "auth.sara.ai"] },
    checkout: {
      worker: "checkout",
      hosts: ["checkout.read-by-ear.com", "checkout.sara.ai"],
    },
    // The worker serving production today from ../retells; deploying this
    // name is the rename-in-place cutover (kanban #4).
    retells: { worker: "retells", hosts: ["retells.read-by-ear.com"] },
    // Reserves the worker name and the monorepo host its README names.
    corpus: { worker: "corpus", hosts: ["corpus.sara.ai"] },
    // One host, no read-by-ear.com mirror: only our Workers call it. Deploying
    // this name is the cutover off the monorepo and also moves a live
    // Hyperdrive credential.
    secure: { worker: "secure", hosts: ["secure.sara.ai"] },
    // read-by-ear.com first: it has the external contract (the /enroll
    // canonical and the printed bookmark QR).
    //
    // `www.sara.ai` is deliberately not claimed: the monorepo redirects it to
    // the apex, and claiming it would turn that redirect into a second live
    // copy of the site. Adding it is one line here and one in
    // apps/www/wrangler.jsonc.
    www: { worker: "www", hosts: ["read-by-ear.com", "sara.ai"] },
    // Same hosts, scoped to the funnel; see STUDY_PATHS.
    study: {
      worker: "study",
      hosts: ["read-by-ear.com", "sara.ai"],
      paths: [...STUDY_PATHS],
    },
    // go.sara.ai has live monorepo traffic (a next.config host rewrite), so
    // deploying its custom domain IS that cutover; go.read-by-ear.com is new.
    // The apex claims are zone routes beside www's custom domains, so they
    // wait on www's own prod deploy (docs/plan/go-app-port.md, phase 4).
    go: {
      worker: "go",
      hosts: ["go.read-by-ear.com", "go.sara.ai"],
      routeClaims: [
        { host: "read-by-ear.com", paths: [...GO_APEX_PATHS] },
        { host: "sara.ai", paths: [...GO_APEX_PATHS] },
      ],
    },
    // Production on fugl.dev by decision (docs/plan/yominoki-port.md,
    // decision 1), so `assertDeployableHosts` never fires for these rows:
    // only the explicit `--env prod` separates a prod deploy from a dev one.
    yominoki: { worker: "yominoki", hosts: ["yominoki.fugl.dev"] },
    // On fugl.dev for the same reason. The live twin at ../academics/lekterion
    // serves this host under this worker name, so deploying it is the cutover
    // in place, as for retells (docs/ops/deploy-playbook.md § apps/lekterion).
    lekterion: { worker: "lekterion", hosts: ["lekterion.fugl.dev"] },
    // Deploying this name IS the ingest cutover: the DO cursor, D1 queue and
    // CockroachDB follow the worker name.
    "yominoki-ingest": {
      worker: "yominoki-ingest",
      hosts: ["yominoki-ingest.fugl.dev"],
    },
    // Reserved, not planned: the table is total. sara.ai because
    // `redirectDomainsFor("prod")` admits only the product zones and the
    // named PROD_FUGL_HOSTS, so a prod lab elsewhere on fugl.dev could never
    // sign in; not read-by-ear.com because this is a team tool.
    "speech-lab": { worker: "speech-lab", hosts: ["speech-lab.sara.ai"] },
    // ../app serves this host from Vercel today, so deploying this name is
    // the cutover off ../app; the custom domain cannot attach until Vercel's
    // DNS record is removed. Worker `reader`, not `app`, because the host is
    // ../app's printed-bookmark contract and the worker name is ours.
    reader: { worker: "reader", hosts: ["app.read-by-ear.com"] },
    // sara.ai, as for the other Workers only our own code calls. Deploys with
    // the production convex-reader it drains, never before.
    "research-ingest": {
      worker: "research-ingest",
      hosts: ["research-ingest.sara.ai"],
    },
    // Reserves the worker name and the host the monorepo serves today; a QR
    // printed on a sheet encodes the serving origin, so no mirror host until
    // one is wanted for as long as its paper exists.
    worksheets: { worker: "worksheets", hosts: ["worksheets.sara.ai"] },
    // parent and my are live on Vercel today (words has no DNS record), so
    // deploying this name is the cutover off the monorepo.
    redirects: {
      worker: "redirects",
      hosts: ["words.sara.ai", "parent.sara.ai", "my.sara.ai"],
    },
    // The monorepo serves this host from Vercel today, so deploying this name
    // is the cutover off the monorepo; the custom domain cannot attach until
    // Vercel's DNS record is removed.
    classroom: { worker: "classroom", hosts: ["classroom.sara.ai"] },
  },
};

/**
 * The monorepo's live brokers, which a staging deploy may still be switched
 * to until the ported brokers serve sign-in (see `allowedPeersFor`).
 */
export const SOURCE_PEERS: PeerSet = {
  authOrigin: "https://auth.sara.ai",
  authIssuer: "auth.sara.ai",
  authJwks: "https://auth.sara.ai/.well-known/jwks.json",
  checkoutOrigin: "https://checkout.sara.ai",
  checkoutIssuer: "checkout.sara.ai",
};

export function originOf(host: string): string {
  return `https://${host}`;
}

export function jwksUrlOf(host: string): string {
  return `${originOf(host)}/.well-known/jwks.json`;
}

/**
 * `app` at `target`, or null when the app has no deploy there (a
 * STAGING_ONLY_APPS app at prod). The way to index the table with an
 * arbitrary `TopologyApp`, which does not compile at prod on purpose.
 */
export function deploymentAt(
  app: TopologyApp,
  target: DeployTarget,
): AppDeployment | null {
  if (target !== "prod") return TOPOLOGY[target][app];
  return isStagingOnly(app) ? null : TOPOLOGY.prod[app];
}

/**
 * The canonical host of `app` at `target`. Overloaded so a call that could
 * land on an absent production entry does not compile; the throw is
 * unreachable from typed code.
 */
export function canonicalHost(app: ProductionApp, target: DeployTarget): string;
export function canonicalHost(app: TopologyApp, target: StagingTarget): string;
export function canonicalHost(app: TopologyApp, target: DeployTarget): string {
  const deployment = deploymentAt(app, target);
  if (deployment === null) {
    throw new Error(`${app} has no ${target} deploy (STAGING_ONLY_APPS).`);
  }
  return deployment.hosts[0];
}

/** `target`'s own auth and checkout. */
function peersAt(target: DeployTarget): PeerSet {
  const auth = canonicalHost("auth", target);
  const checkout = canonicalHost("checkout", target);
  return {
    authOrigin: originOf(auth),
    authIssuer: auth,
    authJwks: jwksUrlOf(auth),
    checkoutOrigin: originOf(checkout),
    checkoutIssuer: checkout,
  };
}

/**
 * The peer set an app uses at a target by default: the target's own. Preview
 * runs preview code against the production backends, whose deployments trust
 * the preview broker alongside the production one, so preview consumers pin
 * the preview broker and the preview checkout rather than dev's.
 */
export function peersFor(target: DeployTarget): PeerSet {
  return peersAt(target);
}

/**
 * Every peer set a target's config may legitimately name. Staging may be
 * switched to the source peers; production is its own set only. Preview
 * names its own auth and checkout by default: its Workers run preview code
 * against the production backends, whose deployments trust the preview
 * broker alongside the production one.
 *
 * The source entry exists because until a human has watched a reader sign in
 * through the ported broker on auth.fugl.dev (kanban #69), the source brokers
 * are the only configuration in which a staging retells can sign anyone in.
 * Delete it once that loop is proven, and the switch stops being possible in
 * the same commit: removing it makes the switch fail the wrangler invariants
 * rather than a review.
 */
export function allowedPeersFor(target: DeployTarget): readonly PeerSet[] {
  const own = peersFor(target);
  if (target === "prod") return [own];
  return [own, SOURCE_PEERS];
}

/** All hosts `app` is served on, across every target. */
export function hostsOf(app: TopologyApp): readonly string[] {
  return DEPLOY_TARGETS.flatMap((target) => hostsAt(app, target));
}

/**
 * The hosts `app` is served on at ONE target, for a deployment that knows
 * which target it is. `hostsOf`'s union is wrong from inside a deployment
 * ("may I sign for the host this request arrived on?"). Empty where the app
 * has no deploy.
 */
export function hostsAt(
  app: TopologyApp,
  target: DeployTarget,
): readonly string[] {
  return deploymentAt(app, target)?.hosts ?? [];
}

/**
 * The targets that share one backend deployment with `target`. A Convex
 * package has two deployments where a Worker has three scopes: dev and
 * preview share the staging one (docs/ops/deploy-playbook.md,
 * `infra/scripts/env-surfaces.ts`), so an allowlist narrowed to one target
 * would turn away the other staging host.
 */
export function backendTargets(target: DeployTarget): readonly DeployTarget[] {
  return target === "prod" ? ["prod"] : ["dev", "preview"];
}

/** The target whose hosts include `host`, or null for a host nothing serves. */
export function targetOfHost(host: string): DeployTarget | null {
  const wanted = host.toLowerCase();
  for (const target of DEPLOY_TARGETS) {
    for (const app of TOPOLOGY_APPS) {
      if (hostsAt(app, target).includes(wanted)) return target;
    }
  }
  return null;
}

/**
 * The app that owns the WHOLE of `host`, or null. Path-scoped apps are
 * skipped so the answer stays single when two apps share a hostname:
 * `appOfHost("read-by-ear.com")` is `"www"`. Use `appOfRequest` when the
 * path is known.
 *
 * `targetOfHost` is deliberately not narrowed: apps sharing a host share its
 * target, and skipping path-scoped rows would make a host owned only by them
 * unresolvable.
 */
export function appOfHost(host: string): TopologyApp | null {
  const wanted = host.toLowerCase();
  for (const target of DEPLOY_TARGETS) {
    for (const app of TOPOLOGY_APPS) {
      const deployment = deploymentAt(app, target);
      if (deployment === null) continue;
      if (deployment.paths === undefined && deployment.hosts.includes(wanted)) {
        return app;
      }
    }
  }
  return null;
}

/**
 * Whether `pathname` is claimed by the single route `pattern`: exact
 * `/enroll` matches only itself, `/enroll/*` the subtree but not the bare
 * path, `/enroll*` anything beginning `/enroll`.
 *
 * Cloudflare tests the full URL, query string included, so an exact pattern
 * that agrees with this function on every pathname still refuses
 * `/enroll?utm_source=...` at the edge. That is why STUDY_PATHS spells its
 * page claims as globs.
 *
 * Exported for its own test: nothing routed through `appOfRequest` can show
 * that an exact claim stops at its own path.
 */
export function pathMatches(pattern: string, pathname: string): boolean {
  // slice(0, -1) drops only the `*`: a subtree pattern keeps its slash, which
  // keeps `/enroll/*` off `/enrollment-policy`; a glob keeps none.
  return pattern.endsWith("*")
    ? pathname.startsWith(pattern.slice(0, -1))
    : pathname === pattern;
}

/**
 * The app that answers `pathname` on `host`; a path-scoped claim beats the
 * catch-all. Cloudflare's own route matching, not this function, implements
 * that in production, so agreement here proves the TABLE is unambiguous, not
 * that the platform obeys it (hence the plan's curl checks).
 */
export function appOfRequest(
  host: string,
  pathname: string,
): TopologyApp | null {
  const wanted = host.toLowerCase();
  let catchAll: TopologyApp | null = null;
  for (const target of DEPLOY_TARGETS) {
    for (const app of TOPOLOGY_APPS) {
      const deployment = deploymentAt(app, target);
      if (deployment === null) continue;
      // A routeClaim is on a host the row does NOT hold, so check it before
      // the own-host narrowing.
      for (const claim of deployment.routeClaims ?? []) {
        if (
          claim.host === wanted &&
          claim.paths.some((pattern) => pathMatches(pattern, pathname))
        ) {
          return app;
        }
      }
      if (!deployment.hosts.includes(wanted)) continue;
      if (deployment.paths === undefined) {
        catchAll ??= app;
      } else if (
        deployment.paths.some((pattern) => pathMatches(pattern, pathname))
      ) {
        return app;
      }
    }
  }
  return catchAll;
}

/**
 * The path prefixes `app` claims at `target`, or null when it claims whole
 * hosts or has no deploy. Own hosts only; see `routeClaimsAt` for claims.
 */
export function pathsAt(
  app: TopologyApp,
  target: DeployTarget,
): readonly string[] | null {
  return deploymentAt(app, target)?.paths ?? null;
}

/** The claims `app` holds on OTHER rows' hosts at `target`. */
export function routeClaimsAt(
  app: TopologyApp,
  target: DeployTarget,
): readonly RouteClaim[] {
  return deploymentAt(app, target)?.routeClaims ?? [];
}

/**
 * Exactly the `pattern` strings `app`'s wrangler scope for `target` must
 * carry: one zone route per (host, path) pair for a path-scoped app, and the
 * BARE HOSTS for a catch-all, because the pinned wrangler (4.123.0) refuses
 * a custom-domain pattern with `*` or a path (`validateRoutes` in
 * node_modules/wrangler/wrangler-dist/cli.js).
 *
 * A row with `routeClaims` gets its bare hosts, then one zone route per
 * claim. A pattern carrying `/` is a zone route and a bare one a custom
 * domain, the partition `@bindery/config/wrangler-invariants` checks by.
 *
 * Empty for an app with no deploy at `target`.
 */
export function routePatternsAt(
  app: TopologyApp,
  target: DeployTarget,
): readonly string[] {
  const deployment = deploymentAt(app, target);
  if (deployment === null) return [];
  const { hosts, paths, routeClaims } = deployment;
  const own =
    paths === undefined
      ? hosts
      : hosts.flatMap((host) => paths.map((path) => `${host}${path}`));
  const claimed = (routeClaims ?? []).flatMap((claim) =>
    claim.paths.map((path) => `${claim.host}${path}`),
  );
  return claimed.length === 0 ? own : [...own, ...claimed];
}

/**
 * Every auth or checkout host a consumer might be redirected through, at any
 * target, source peers included, so a page can tell its own redirects from
 * an acquisition-source referrer.
 */
export function isPeerHost(host: string): boolean {
  const wanted = host.toLowerCase();
  if (
    wanted === new URL(SOURCE_PEERS.authOrigin).hostname ||
    wanted === new URL(SOURCE_PEERS.checkoutOrigin).hostname
  ) {
    return true;
  }
  const app = appOfHost(wanted);
  return app === "auth" || app === "checkout";
}

/**
 * The fugl.dev hosts the PRODUCTION broker admits, each by exact name. Not a
 * `*.fugl.dev` wildcard: that would hand prod exchange codes to every staging
 * app on the zone. Personal tools and the fugl.dev-hosted production apps.
 */
export const PROD_FUGL_HOSTS = [
  "t3.fugl.dev",
  "lekterion.fugl.dev",
  "yominoki.fugl.dev",
  "code.fugl.dev",
  "git.fugl.dev",
  "embed.fugl.dev",
  "ollama.fugl.dev",
  "fitness.fugl.dev",
] as const;

/**
 * The broker's redirect-domain allowlist at a target. The wildcards also
 * cover the broker's own hosts and `go`'s, which the broker refuses by check
 * instead: apps/auth's `isDomainAllowed` (kanban #98) and
 * @bindery/shared/auth/redirectors (kanban #232).
 */
export function redirectDomainsFor(target: DeployTarget): readonly string[] {
  return target === "prod"
    ? ["*.read-by-ear.com", "*.sara.ai", ...PROD_FUGL_HOSTS]
    : ["*.fugl.dev"];
}

/**
 * The Cloudflare zones every host in the table sits in; a zone route names
 * its zone in `zone_name`, and naming the wrong one deploys and serves
 * nothing. The order is not load-bearing: the zones are disjoint.
 */
export const ZONES = ["fugl.dev", "read-by-ear.com", "sara.ai"] as const;
export type Zone = (typeof ZONES)[number];

/**
 * The zone `host` belongs to, or null. The boundary is the dot: `evil-sara.ai`
 * is not in `sara.ai`, as in apps/www's `retellsOriginForHost`.
 */
export function zoneOf(host: string): Zone | null {
  const wanted = host.toLowerCase();
  for (const zone of ZONES) {
    if (wanted === zone || wanted.endsWith(`.${zone}`)) return zone;
  }
  return null;
}
